/**
 * The two things every service in this directory needs before it can make a
 * request: where the API lives, and how to prove who you are.
 *
 * Both were copied rather than shared — `getAuthHeaders` existed four times
 * over (adminService, userDetailService, accountService, mfaService) and
 * `const API_BASE_URL = '/api'` six times. The copies never disagreed on
 * behaviour, but a base URL that has to be changed in six places is a base URL
 * that will one day be changed in five.
 *
 * Deliberately just these two values. There is no request helper here, and
 * adding one would be a mistake rather than the obvious next step: a shared
 * wrapper would have to take a position on what a 401 means, and the whole
 * reason authService and mfaService are siblings instead of one module is that
 * they hold *opposite* positions on exactly that — mfaService clears the
 * session and redirects, authService must never do so or the login page sends
 * itself to itself. See the module comments on both.
 */

export const API_BASE_URL = '/api';

/**
 * Bearer headers for the endpoints that require a signed-in user.
 *
 * The token is read at call time rather than captured at module load, because
 * it can be replaced under a live module — a second sign-in in the same tab
 * writes a new one, and a 401 removes it.
 *
 * postLogin deliberately does not use this and builds its headers by hand: it
 * authorises with the token it was just handed, which is not necessarily the
 * one in storage yet. Reading it back from localStorage would make that call
 * depend on store() having already run, an ordering nobody would think to
 * preserve.
 */
export const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
});
